import { useState, useEffect, useMemo, useCallback } from "react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { debounce } from "@/lib/utils/debounce";
import { SearchResponse } from "@/lib/services/searchService";
import Button from "@/components/ui/Button";

interface SearchBarProps {
  onSearch: (query: string) => Promise<SearchResponse>;
  placeholder?: string;
  defaultValue?: string;
  className?: string;
  autoSearch?: boolean; // If true, search triggers on type (with debounce)
}

export default function SearchBar({
  onSearch,
  placeholder = "Search for items, locations, or categories...",
  defaultValue = "",
  className = "",
  autoSearch = false,
}: SearchBarProps) {
  const [searchQuery, setSearchQuery] = useState(defaultValue);
  const [isLoading, setIsLoading] = useState(false);

  const debounceMs = autoSearch ? 400 : 300;
  const debouncedSearch = useMemo(
    () =>
      debounce(
        ((query: string) => {
          setIsLoading(true);
          onSearch(query)
            .finally(() => {
              setIsLoading(false);
            })
            .catch(() => {});
        }) as (...args: unknown[]) => unknown,
        debounceMs,
      ),
    [onSearch, debounceMs],
  );

  const handleClear = useCallback(() => {
    setSearchQuery("");
    setIsLoading(true);
    onSearch("")
      .finally(() => {
        setIsLoading(false);
      })
      .catch(() => {});
  }, [onSearch]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    if (autoSearch) {
      debouncedSearch(newQuery);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onSearch(searchQuery);
    } finally {
      setIsLoading(false);
    }
  };

  // Sync local state from URL/parent (defaultValue). Do not trigger search here—
  // the parent already has the search term and SWR will fetch; triggering onSearch
  // would cause redundant requests and double-trigger when handleSearch identity changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(defaultValue);
    }, 0);
    return () => clearTimeout(timer);
  }, [defaultValue]);

  return (
    <form onSubmit={handleSubmit} className={`relative ${className}`}>
      <div className="relative overflow-hidden rounded-lg border border-border focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent">
        <MagnifyingGlassIcon
          className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 z-10 ${
            isLoading ? "text-primary animate-pulse" : "text-text-light"
          }`}
        />
        <input
          autoFocus={false}
          id="search-input"
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="w-full pl-10 py-3 pr-4 border-0 bg-surface focus:ring-0 focus:outline-none rounded-lg"
          style={{ paddingRight: searchQuery.length > 0 ? "7.5rem" : "2.5rem" }}
          aria-label="Search items"
          autoComplete="off"
        />
        {searchQuery.length > 0 && (
          <div className="absolute right-0 top-0 bottom-0 flex items-center gap-0.5 pr-2 pointer-events-none">
            <div className="pointer-events-auto flex items-center">
              <button
                type="button"
                onClick={handleClear}
                className="p-2 rounded-md text-text-light hover:text-text-light hover:bg-tertiary focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <Button
                type="submit"
                variant="ghost"
                className="px-3 py-1.5 rounded-lg hover:bg-primary-dark transition text-primary-600 border-primary-600 text-sm shrink-0"
                disabled={isLoading}
              >
                Search
              </Button>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
